import React, { createContext, useState, useContext, useEffect } from 'react';
import { API, Auth } from 'aws-amplify';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import { resolveAppSub } from '../utils/appIdentity';

const OrganizationContext = createContext(null);

// Decide which organization should be active given the freshly fetched list and any stored choice.
// Returns null when the user has NO organizations — that null is what clears a stale active org (the
// previous code removed the localStorage key but left the STATE set, leaking a prior org into the UI).
export const resolveActiveOrg = (allOrgs, storedOrg) => {
  if (!Array.isArray(allOrgs) || allOrgs.length === 0) return null;
  if (storedOrg) {
    const match = allOrgs.find((o) => o.id === storedOrg.id);
    if (match) return match;
  }
  return allOrgs.find((o) => o.isActive) || allOrgs[0];
};

export const OrganizationProvider = ({ children }) => {
  const [activeOrganization, setActiveOrganization] = useState(() => {
    // Try to get the active organization from localStorage on initial load
    const storedOrg = localStorage.getItem('activeOrganization');
    return storedOrg ? JSON.parse(storedOrg) : null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  // True only once the org list has been fetched SUCCESSFULLY at least once.
  // Guards against treating a transient fetch error (which leaves organizations
  // at []) as "user has no org" and wrongly redirecting them to /account-setup.
  const [orgsFetched, setOrgsFetched] = useState(false);

  // Update localStorage whenever activeOrganization changes
  useEffect(() => {
    if (activeOrganization) {
      localStorage.setItem('activeOrganization', JSON.stringify(activeOrganization));
    } else {
      localStorage.removeItem('activeOrganization');
    }
  }, [activeOrganization]);

  // Fire-and-forget update of lastAccessedAt for an organization
  const updateLastAccessedAt = async (orgId, version) => {
    try {
      await API.graphql({
        query: mutations.updateOrganization,
        variables: {
          input: {
            id: orgId,
            lastAccessedAt: new Date().toISOString(),
            _version: version
          }
        }
      });
    } catch (err) {
      // Non-critical, don't block user flow
      console.warn('Failed to update lastAccessedAt:', err);
    }
  };

  const fetchUserOrganizations = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const user = await Auth.currentAuthenticatedUser();
      // Not user.attributes.sub: memberships and Organization.owner store the sub this person's data
      // was created under, and a migration to the FITTWorks hub pool changes the sub on the token but
      // not the sub in the rows. Filtering on the token's sub would return nothing for a migrated
      // owner and drop them into the create-your-first-organization flow on top of their real one.
      const { sub: userSub } = await resolveAppSub(user);

      // Get organizations where user is a member (paginate to avoid missing records)
      let allMemberItems = [];
      let memberNextToken = null;
      do {
        const memberOrgsResponse = await API.graphql({
          query: queries.listOrganizationMembers,
          variables: {
            filter: {
              userSub: { eq: userSub }
            },
            limit: 100,
            nextToken: memberNextToken
          }
        });
        const page = memberOrgsResponse.data.listOrganizationMembers;
        allMemberItems = [...allMemberItems, ...page.items];
        memberNextToken = page.nextToken;
      } while (memberNextToken);

      // Get organizations owned by user (paginate to avoid missing records)
      let allOwnedItems = [];
      let ownedNextToken = null;
      do {
        const ownedPage = await API.graphql({
          query: queries.listOrganizations,
          variables: {
            filter: { owner: { eq: userSub } },
            limit: 100,
            nextToken: ownedNextToken
          }
        });
        const ownedResult = ownedPage.data.listOrganizations;
        allOwnedItems = [...allOwnedItems, ...ownedResult.items];
        ownedNextToken = ownedResult.nextToken;
      } while (ownedNextToken);

      const memberOrgsResults = await Promise.allSettled(
        allMemberItems
          .filter(member => !member._deleted)
          .map(async (member) => {
            if (member.organizationID) {
              const orgResponse = await API.graphql({
                query: `
                  query GetOrganization($id: ID!) {
                    getOrganization(id: $id) {
                      id
                      name
                      owner
                      additionalOwners
                      contactEmail
                      contactPhone
                      location
                      coordinates
                      logo
                      isActive
                      leaderboardEnabled
                      learningCoinsPerInterval
                      learningCoinInterval
                      learningMaxCoinsPerSession
                      learningCoinsEnabled
                      stripeCustomerId
                      stripeSubscriptionId
                      stripeSubscriptionItemId
                      subscriptionStatus
                      subscriptionPeriodEnd
                      billingPeriod
                      activeUserCount
                      purchasedLicenses
                      billingBypass
                      billingBypassExpiry
                      billingBypassGrantedBy
                      billingBypassReason
                      preBillingBypassLicenses
                      aiDisabledUsers
                      lastAccessedAt
                      createdAt
                      updatedAt
                      _version
                      _deleted
                      _lastChangedAt
                    }
                  }
                `,
                variables: { id: member.organizationID }
              });
              return orgResponse.data.getOrganization;
            }
            return null;
          })
      );

      const validMemberOrgs = memberOrgsResults
        .filter(r => r.status === 'fulfilled' && r.value !== null && !r.value._deleted)
        .map(r => r.value);
      const ownedOrgs = allOwnedItems.filter(org => !org._deleted);
      
      const allOrgs = [...validMemberOrgs, ...ownedOrgs];
      setOrganizations(allOrgs);
      // Mark that we now have an authoritative answer on org membership.
      setOrgsFetched(true);

      const storedRaw = localStorage.getItem('activeOrganization');
      const storedOrg = storedRaw ? JSON.parse(storedRaw) : null;
      const storedValid = storedOrg && allOrgs.some((o) => o.id === storedOrg.id);
      const nextActive = resolveActiveOrg(allOrgs, storedOrg);

      setActiveOrganization(nextActive);
      if (nextActive) {
        localStorage.setItem('activeOrganization', JSON.stringify(nextActive));
        updateLastAccessedAt(nextActive.id, nextActive._version);
        // Persist the isActive flag only on the first-org fallback (no valid stored pick and no org
        // already flagged isActive) — mirrors the prior cross-device behavior.
        if (!storedValid && !nextActive.isActive) {
          await setOrganizationAsActive(nextActive.id);
        }
      } else {
        // No organizations (or the stored one is gone): clear the active org from STATE, not just
        // from localStorage. Leaving the state set is what leaked a stale org behind the create-first
        // modal for a brand-new user.
        localStorage.removeItem('activeOrganization');
      }

    } catch (error) {
      console.error('Error fetching organizations:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const setOrganizationAsActive = async (organizationId) => {
    try {
      // First, deactivate the currently active organization if exists
      if (activeOrganization?.id) {
        await API.graphql({
          query: mutations.updateOrganization,
          variables: {
            input: {
              id: activeOrganization.id,
              isActive: false,
              _version: activeOrganization._version
            }
          }
        });
      }

      // Then activate the new organization
      const result = await API.graphql({
        query: mutations.updateOrganization,
        variables: {
          input: {
            id: organizationId,
            isActive: true
          }
        }
      });

      return result.data.updateOrganization;
    } catch (error) {
      console.error('Error setting organization as active:', error);
      throw error;
    }
  };

  const updateActiveOrganization = async (organizationId) => {
    setIsLoading(true);
    try {
      const updatedOrg = await setOrganizationAsActive(organizationId);
      const result = await API.graphql({
        query: `
          query GetOrganization($id: ID!) {
            getOrganization(id: $id) {
              id
              name
              owner
              additionalOwners
              contactEmail
              contactPhone
              location
              coordinates
              logo
              isActive
              leaderboardEnabled
              learningCoinsPerInterval
              learningCoinInterval
              learningMaxCoinsPerSession
              learningCoinsEnabled
              stripeCustomerId
              stripeSubscriptionId
              stripeSubscriptionItemId
              subscriptionStatus
              subscriptionPeriodEnd
              billingPeriod
              activeUserCount
              purchasedLicenses
              billingBypass
              billingBypassExpiry
              billingBypassGrantedBy
              billingBypassReason
              preBillingBypassLicenses
              aiDisabledUsers
              lastAccessedAt
              createdAt
              updatedAt
              _version
              _deleted
              _lastChangedAt
            }
          }
        `,
        variables: { id: organizationId }
      });
      const org = result.data.getOrganization;
      setActiveOrganization(org);

      // Update lastAccessedAt timestamp
      updateLastAccessedAt(org.id, org._version);
    } catch (error) {
      console.error('Error updating active organization:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshOrganization = async () => {
    if (activeOrganization?.id) {
      await updateActiveOrganization(activeOrganization.id);
    }
  };

  // Check if organization trial has expired (14 days with no licenses)
  const isTrialExpired = (organization) => {
    if (!organization) return false;
    
    // If organization has an active billing bypass, check expiry
    if (organization.billingBypass === true) {
      const bypassExpiry = organization.billingBypassExpiry ? new Date(organization.billingBypassExpiry) : null;
      if (bypassExpiry && bypassExpiry > new Date()) {
        // Billing bypass is active and not expired — not trial expired
        return false;
      }
      // Bypass has expired — fall through to normal checks but ignore inflated purchasedLicenses
      // (purchasedLicenses might still be 9999 from the bypass)
      const createdAt = new Date(organization.createdAt);
      const now = new Date();
      const daysDifference = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));
      return daysDifference >= 14;
    }
    
    // If organization has purchased licenses, trial is not expired
    if (organization.purchasedLicenses > 0) return false;
    
    // Calculate days since creation
    const createdAt = new Date(organization.createdAt);
    const now = new Date();
    const daysDifference = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));
    
    // Trial expires after 14 days with no licenses
    return daysDifference >= 14;
  };

  // Check if organization needs to be redirected to management page
  const needsLicenseManagement = (organization) => {
    return isTrialExpired(organization);
  };

  // Fetch organizations when the component mounts
  useEffect(() => {
    fetchUserOrganizations();
  }, []);

  return (
    <OrganizationContext.Provider 
      value={{ 
        activeOrganization, 
        setActiveOrganization,
        updateActiveOrganization,
        refreshOrganization,
        isLoading,
        error,
        organizations,
        hasOrganizations: organizations.length > 0,
        orgsFetched,
        fetchUserOrganizations,
        isTrialExpired,
        needsLicenseManagement
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

export default OrganizationContext; 